import { ReactElement, useMemo } from 'react';
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { ChartTooltip } from '@Components/Charts/ChartTooltip';
import { Neutral500 } from '../../utils/colors';
import { AdPerformanceRow } from './metaHackathon-performance';
import {
  ARCHETYPE_LABEL,
  AssetSeries,
  DiversityArchetypeType,
  POINT_RADIUS,
  QUADRANT_MIDPOINT,
  TRAIL_LENGTH,
  TRAIL_MIN_OPACITY
} from './metaHackathon-constants';
import { campaignForAsset, interpolatePoint, quadrantOf } from './metaHackathon-utils';

interface ChartPoint {
  fatigue: number;
  diversity: number;
  assetId: string;
  assetName: string;
  campaign: string;
  color: string;
  date: string;
  archetype: DiversityArchetypeType;
}

interface QuadrantScatterChartProps {
  series: AssetSeries[];
  cursor: number;
  dates: string[];
  assetColors: Map<string, string>; // assetId -> color, per the active color mode
  hiddenAssetIds: Set<string>;
  performance?: Map<string, AdPerformanceRow>;
}

// Spacing (in cursor/date units) between successive trail samples.
const TRAIL_SPACING = 0.6;

const frameAt = (
  series: AssetSeries[],
  cursor: number,
  dates: string[],
  assetColors: Map<string, string>,
  performance?: Map<string, AdPerformanceRow>
): ChartPoint[] => {
  const frameDate = dates[Math.min(dates.length - 1, Math.max(0, Math.round(cursor)))] ?? '';
  return series.map(s => {
    const { fatigue, diversity } = interpolatePoint(s, cursor);
    return {
      fatigue,
      diversity,
      assetId: s.assetId,
      assetName: s.assetName,
      campaign: campaignForAsset(s, performance),
      color: assetColors.get(s.assetId) || Neutral500,
      date: frameDate,
      archetype: s.archetype
    };
  });
};

interface PointShapeProps {
  cx?: number;
  cy?: number;
  payload?: ChartPoint;
}

const makePointShape = (radius: number, opacity: number, outlined: boolean) =>
  function PointShape({ cx, cy, payload }: PointShapeProps): ReactElement {
    if (cx === undefined || cy === undefined || !payload) return <g />;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={payload.color}
        fillOpacity={opacity}
        stroke={outlined ? 'white' : 'none'}
        strokeWidth={outlined ? 1.5 : 0}
      />
    );
  };

const formatAxis = (value: number): string => {
  if (value <= 0) return 'Low';
  if (value >= 1) return 'High';
  return 'Mid';
};

const QuadrantTooltip = ({
  active,
  payload,
  performance
}: {
  active?: boolean;
  payload?: { payload: ChartPoint }[];
  performance?: Map<string, AdPerformanceRow>;
}) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  const metrics = performance?.get(point.assetId);
  const performanceItems = metrics
    ? [
        { label: 'Spend', value: formatMoney(metrics.spend), color: point.color },
        { label: 'Impressions', value: formatNumber(metrics.impressions), color: point.color },
        { label: 'Clicks', value: formatNumber(metrics.clicks), color: point.color },
        { label: 'Revenue', value: formatMoney(metrics.revenue), color: point.color }
      ]
    : [];

  return (
    <ChartTooltip
      headerLabel={point.assetName}
      items={[
        { label: 'Campaign', value: point.campaign, color: point.color },
        { label: 'Ad name', value: point.assetName, color: point.color },
        { label: 'Date', value: point.date, color: point.color },
        { label: 'Fatigue', value: point.fatigue.toFixed(2), color: point.color },
        { label: 'Diversity', value: point.diversity.toFixed(2), color: point.color },
        { label: 'Diversity trend', value: ARCHETYPE_LABEL[point.archetype], color: point.color },
        {
          label: 'Quadrant',
          value: quadrantOf(point.fatigue, point.diversity),
          color: point.color
        },
        ...performanceItems
      ]}
    />
  );
};

export const QuadrantScatterChart = ({
  series,
  cursor,
  dates,
  assetColors,
  hiddenAssetIds,
  performance
}: QuadrantScatterChartProps) => {
  const visibleSeries = useMemo(
    () => series.filter(s => !hiddenAssetIds.has(s.assetId)),
    [series, hiddenAssetIds]
  );

  const currentFrame = useMemo(
    () => frameAt(visibleSeries, cursor, dates, assetColors, performance),
    [visibleSeries, cursor, dates, assetColors, performance]
  );

  // Older positions, drawn first (underneath) with decreasing opacity so each
  // point leaves a short fading wake of where it has been.
  const trailFrames = useMemo(
    () =>
      Array.from({ length: TRAIL_LENGTH }, (_, idx) => {
        const depth = idx + 1;
        const trailCursor = cursor - depth * TRAIL_SPACING;
        const opacity =
          TRAIL_MIN_OPACITY + (1 - TRAIL_MIN_OPACITY) * (1 - depth / (TRAIL_LENGTH + 1));
        const radius = POINT_RADIUS * (1 - 0.4 * (depth / (TRAIL_LENGTH + 1)));
        return {
          data: frameAt(visibleSeries, trailCursor, dates, assetColors, performance),
          opacity,
          radius,
          visible: trailCursor >= 0
        };
      }),
    [visibleSeries, cursor, dates, assetColors, performance]
  );

  return (
    <div className='quadrant-chart'>
      <span className='quadrant-label top-left'>High Diversity · Low Fatigue</span>
      <span className='quadrant-label top-right'>High Diversity · High Fatigue</span>
      <span className='quadrant-label bottom-left'>Low Diversity · Low Fatigue</span>
      <span className='quadrant-label bottom-right'>Low Diversity · High Fatigue</span>
      <ResponsiveContainer width='100%' height='100%'>
        <ScatterChart margin={{ top: 16, right: 24, bottom: 24, left: 8 }}>
          <CartesianGrid stroke={CARTESIAN_GRID_STROKE} strokeWidth={CARTESIAN_GRID_STROKE_WIDTH} />
          <XAxis
            type='number'
            dataKey='fatigue'
            name='Fatigue'
            domain={[0, 1]}
            ticks={[0, QUADRANT_MIDPOINT, 1]}
            tickFormatter={formatAxis}
            tick={TICK_STYLE}
            label={{ value: 'Fatigue →', position: 'insideBottom', offset: -12, fill: Neutral500 }}
          />
          <YAxis
            type='number'
            dataKey='diversity'
            name='Diversity'
            domain={[0, 1]}
            ticks={[0, QUADRANT_MIDPOINT, 1]}
            tickFormatter={formatAxis}
            tick={TICK_STYLE}
            label={{
              value: 'Diversity →',
              angle: -90,
              position: 'insideLeft',
              fill: Neutral500
            }}
          />
          <ReferenceLine x={QUADRANT_MIDPOINT} stroke={Neutral500} strokeDasharray='4 4' />
          <ReferenceLine y={QUADRANT_MIDPOINT} stroke={Neutral500} strokeDasharray='4 4' />
          <Tooltip
            content={<QuadrantTooltip performance={performance} />}
            cursor={{ strokeDasharray: '3 3' }}
          />
          {trailFrames.map(
            (frame, idx) =>
              frame.visible && (
                <Scatter
                  key={`trail-${idx}`}
                  data={frame.data}
                  isAnimationActive={false}
                  shape={makePointShape(frame.radius, frame.opacity, false)}
                  legendType='none'
                />
              )
          )}
          <Scatter
            data={currentFrame}
            isAnimationActive={false}
            shape={makePointShape(POINT_RADIUS, 1, true)}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};
